import {
  buildWizardSteps,
  getProgramFieldsBySection,
  getResolvedFieldsForRoomTemplate,
  resolveTemplateQuestion,
} from './scopeTemplateEngine';
import { tipTapDocToPreviewText } from './scopeApiMapper';
import { titleCaseLabel } from './ScopeField';
import { isFieldVisible } from './scopeFieldVisibility';

const formatValue = (value) => {
  if (value && typeof value === 'object' && value.format === 'tiptap') {
    return tipTapDocToPreviewText(value.doc);
  }
  if (Array.isArray(value)) return value.length ? value.join(', ') : '';
  if (value === null || value === undefined || value === '') return '';
  return String(value);
};

const collectFields = (fields, values) =>
  (fields || [])
    .map((field) => {
      if (!isFieldVisible(field, values || {})) return null;
      const display = formatValue(values?.[field.id]);
      if (!display) return null;
      return {
        id: field.id,
        label: titleCaseLabel(field.label),
        value: display,
        isHtml: false,
      };
    })
    .filter(Boolean);

/** Preview sections for PDF from live template + scope document. */
export const buildTemplatePreviewSections = (template, scopeDoc) => {
  if (!template || !scopeDoc) return [];
  const steps = buildWizardSteps(template, scopeDoc);

  return steps
    .map((step) => {
      if (step.kind === 'program') {
        const fields = collectFields(
          step.programFields ||
            getProgramFieldsBySection(template, step.sectionLetter)[0]
              ?.fields ||
            [],
          scopeDoc.program || {}
        );
        if (!fields.length) return null;
        return {
          id: step.id,
          letter: step.letter,
          title: step.title,
          type: 'standard',
          subsections: [
            {
              id: step.id,
              title: step.title,
              fields,
            },
          ],
        };
      }

      if (step.kind === 'document_notes') {
        const text = formatValue(
          scopeDoc.documentNotes || scopeDoc.raw?.document_notes
        );
        if (!text) return null;
        return {
          id: step.id,
          letter: step.letter,
          title: step.title,
          type: 'standard',
          subsections: [
            {
              id: 'notes',
              title: step.title,
              fields: [
                {
                  id: 'document_notes',
                  label: 'Notes',
                  value: text,
                  isHtml: false,
                },
              ],
            },
          ],
        };
      }

      if (step.kind === 'area') {
        const area = scopeDoc.areas?.[step.roomType] || {};
        const groups = getResolvedFieldsForRoomTemplate(
          step.roomTemplate,
          template
        );
        const subsections = [];
        const programFields = collectFields(
          step.programFields || [],
          scopeDoc.program || {}
        );
        if (programFields.length) {
          subsections.push({
            id: 'program',
            title: 'Program',
            fields: programFields,
          });
        }
        groups.forEach((group) => {
          const fields = collectFields(group.fields, area.answers || {});
          if (fields.length) {
            subsections.push({ id: group.id, title: group.title, fields });
          }
        });
        if (!subsections.length) return null;
        return {
          id: step.id,
          letter: step.letter,
          title: step.title,
          type: 'standard',
          subsections,
        };
      }

      if (step.kind === 'balcony_list') {
        const groups = getResolvedFieldsForRoomTemplate(
          step.roomTemplate,
          template
        );
        const subsections = [];
        const programFields = collectFields(
          step.programFields || [],
          scopeDoc.program || {}
        );
        if (programFields.length) {
          subsections.push({
            id: 'program',
            title: 'Program',
            fields: programFields,
          });
        }
        (step.balconyIndexes || []).forEach((index) => {
          const room = (scopeDoc.rooms || []).find(
            (r) => r.room_type === 'balcony' && Number(r.index) === Number(index)
          );
          if (!room) return;
          if (room.label) {
            subsections.push({
              id: `balcony-${index}-label`,
              title: `Balcony ${index} — Label`,
              fields: [
                {
                  id: 'label',
                  label: 'Balcony label',
                  value: room.label,
                  isHtml: false,
                },
              ],
            });
          }
          groups.forEach((group) => {
            const fields = collectFields(group.fields, room.answers || {});
            if (fields.length) {
              subsections.push({
                id: `balcony-${index}-${group.id}`,
                title: `Balcony ${index} — ${group.title}`,
                fields,
              });
            }
          });
        });
        if (!subsections.length) return null;
        return {
          id: step.id,
          letter: step.letter,
          title: step.title,
          type: 'standard',
          subsections,
        };
      }

      if (step.kind === 'room') {
        const room = (scopeDoc.rooms || []).find(
          (r) =>
            r.room_type === step.roomType &&
            Number(r.index) === Number(step.index)
        );
        if (!room) return null;
        const groups = getResolvedFieldsForRoomTemplate(
          step.roomTemplate,
          template
        );
        const subsections = [];
        const programFields = collectFields(
          step.programFields || [],
          scopeDoc.program || {}
        );
        if (programFields.length) {
          subsections.push({
            id: 'program',
            title: 'Program',
            fields: programFields,
          });
        }
        if (room.label) {
          subsections.push({
            id: 'label',
            title: 'Label',
            fields: [
              {
                id: 'label',
                label: 'Room label',
                value: room.label,
                isHtml: false,
              },
            ],
          });
        }
        groups.forEach((group) => {
          const fields = collectFields(group.fields, room.answers || {});
          if (fields.length) {
            subsections.push({
              id: group.id,
              title: group.title,
              fields,
            });
          }
        });
        if (!subsections.length) return null;
        return {
          id: step.id,
          letter: step.letter,
          title: step.title,
          type: 'standard',
          subsections,
        };
      }

      return null;
    })
    .filter(Boolean);
};

export const resolveProgramField = (question, template) =>
  resolveTemplateQuestion(question, template);
